import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  slideInsufficientTissueService,
  SlideTissueInput,
  SlideTissueRow,
  SlideTissueSource,
} from "../services/slideInsufficientTissue.service";

export function useSlideTissueRows(source: SlideTissueSource, caseId: string) {
  return useQuery({
    queryKey: ["slide-tissue", source, caseId],
    queryFn: () => slideInsufficientTissueService.list(source, caseId),
    enabled: Boolean(caseId),
    staleTime: 5000,
    retry: false,
  });
}

export function useSetSlideInsufficientTissue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SlideTissueInput) =>
      slideInsufficientTissueService.setFlag(input),
    retry: false,
    onSuccess: async (result) => {
      client.setQueriesData<SlideTissueRow[]>(
        { queryKey: ["slide-tissue"] },
        (rows) =>
          rows?.map((row) =>
            row.id === result.slideId
              ? { ...row, insufficientTissue: result.insufficientTissue }
              : row
          )
      );
      const ids = Array.from(
        new Set([result.caseId, result.materialOwnerCaseId])
      );
      const readers = [
        "case",
        "case-specimens",
        "case-blocks",
        "case-slides",
        "case-additional-stains",
        "case-for-pathologist-review",
        "distribution-case-slides",
      ];
      await Promise.all(
        ids.flatMap((id) =>
          readers.map((key) =>
            client.invalidateQueries({ queryKey: [key, id] })
          )
        )
      );
      await Promise.all(
        ["distribution-queue", "pending-distribution"].map((key) =>
          client.invalidateQueries({ queryKey: [key] })
        )
      );
    },
    onSettled: () => client.invalidateQueries({ queryKey: ["slide-tissue"] }),
  });
}
